// MaturaLogSingleCoefClosedTask.js
// Zadanie maturalne zamkniete:
// log_a(X) ± k*log_a(Y), gdzie wspolczynnik jest tylko przy jednym logarytmie.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const pow = Math.pow;

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function logb(a) {
  return `\\log_{${a}}`;
}

function uniqueLatex(values, needed, avoid) {
  const out = [];
  const used = new Set([String(avoid)]);
  for (const v of values) {
    const s = String(v);
    if (used.has(s)) continue;
    used.add(s);
    out.push(s);
    if (out.length >= needed) break;
  }
  return out;
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaLogSingleCoefClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 3,
    },
    data: {
      a: 3,
      k: 2,
      op: "-",
      X: 108,
      Y: 2,
      t: 3,
      exprLatex: "\\log_{3}\\,108-2\\log_{3}\\,2",
      options: [
        { label: "A", latex: "3", isCorrect: true },
        { label: "B", latex: "9", isCorrect: false },
        { label: "C", latex: "\\log_{3}\\,104", isCorrect: false },
        { label: "D", latex: "2\\log_{3}\\,54", isCorrect: false },
      ],
      correctIndex: 0,
      maxPoints: 1,
    },
  };
}

export function generateTask(generateOptions = {}) {
  if (
    generateOptions.variant === "original" &&
    generateOptions.session === "2025_MAJ"
  ) {
    return generateMay2025OriginalTask();
  }

  // Celowo trzymamy wariant odejmowania (CKE-like), zeby nie powstawaly
  // "lokalnie policzalne" uklady typu log_3 9 + 3log_3 3.
  const op = "-";

  // Trzymamy liczby male (bez 4-cyfrowych argumentow).
  // Parametry sa preselektowane tak, by takze "zmylki" nie wybuchaly rozmiarem.
  const minusPool = [];
  for (const a0 of [2, 3, 5]) {
    for (const k0 of [2, 3]) {
      // Y nie moze byc potega podstawy a (w szczegolnosci Y != a),
      // zeby nie dalo sie policzyc drugiego logarytmu "w glowie" bez wlasnosci.
      for (const Y0 of [2, 3, 5, 6, 7]) {
        if (a0 === 2 && [2, 4, 8].includes(Y0)) continue;
        if (a0 === 3 && [3, 9].includes(Y0)) continue;
        if (a0 === 5 && [5].includes(Y0)) continue;
        for (const t0 of [2, 3, 4]) {
          const yPowK0 = pow(Y0, k0);
          const X0 = pow(a0, t0) * yPowK0;
          if (X0 > 999) continue;
          if (X0 - yPowK0 <= 0 || X0 - yPowK0 > 999) continue;
          if (X0 + yPowK0 > 999) continue;
          minusPool.push({ a: a0, k: k0, Y: Y0, t: t0, X: X0 });
        }
      }
    }
  }

  const chosen = pick(minusPool);
  const { a, k } = chosen;

  let X;
  let Y;
  let t;
  let correctValue;

  Y = chosen.Y;
  t = chosen.t;
  X = chosen.X;
  correctValue = t;

  const exprLatex =
    op === "-"
      ? `${logb(a)}\\,${X}-${k}${logb(a)}\\,${Y}`
      : `${logb(a)}\\,${X}+${k}${logb(a)}\\,${Y}`;

  const yPowK = pow(Y, k);
  const splitMode = Math.random() < 0.5 ? "3num1log" : "2num2log";

  // Rdzeń odpowiedzi "jak w oryginale CKE":
  // 1) liczba (zmyłka numeryczna), 2) log z argumentem po +/-, 3) k*log z argumentem po / lub *.
  const numMain = `${pow(a, Math.max(1, t - 1))}`; // np. dla t=3,a=3 daje 9
  const logArg1 = op === "-" ? X - yPowK : X + yPowK;
  const logArg2 = op === "-" ? Math.floor(X / Y) : X * Y;
  const logMain1 =
    op === "-"
      ? `${logb(a)}\\,${logArg1}` // np. log_3 104
      : `${logb(a)}\\,${logArg1}`;
  const logMain2 =
    op === "-"
      ? `${k}${logb(a)}\\,${logArg2}` // np. 2log_3 54
      : `${k}${logb(a)}\\,${logArg2}`;

  // "Piąta" odpowiedź do podmiany z jednym logarytmem (żeby zrobić wariant 3 liczby + 1 log).
  const numSwapPool = [
    `${pow(a, t)}`,
    `${k * t}`,
    `${t + 1}`,
    `${t - 1}`,
  ];
  const numSwap = uniqueLatex(shuffle(numSwapPool), 1, `${correctValue}`)[0] || `${t + 2}`;
  const sameLogArgumentFamily = logArg1 === logArg2;

  let wrong;
  if (splitMode === "2num2log") {
    // 1 poprawna liczba + (1 liczba, 2 logi)
    // Ale jesli oba logi sa na tym samym argumencie (log_a(M) i klog_a(M)),
    // podmieniamy jeden z nich na "dodatkowa" opcje.
    wrong = sameLogArgumentFamily
      ? [numMain, numSwap, logMain1]
      : [numMain, logMain1, logMain2];
  } else {
    // 1 poprawna liczba + (2 liczby, 1 log) — podmieniamy jeden z logów na "piątą" liczbową.
    const keptLog = Math.random() < 0.5 ? logMain1 : logMain2;
    wrong = [numMain, numSwap, keptLog];
  }

  // Dodatkowe zabezpieczenie unikalności względem poprawnej odpowiedzi.
  wrong = uniqueLatex(wrong, 3, `${correctValue}`);
  if (wrong.length < 3) {
    const fallback = uniqueLatex(
      shuffle([`${pow(a, t)}`, `${k * t}`, `${t + 1}`, `${t - 1}`, `${logMain1}`, `${logMain2}`]),
      3 - wrong.length,
      `${correctValue}`
    );
    wrong = uniqueLatex([...wrong, ...fallback], 3, `${correctValue}`);
  }

  // Twarde zabezpieczenie: zawsze 3 bledne odpowiedzi.
  // Gdyby deduplikacja "zjadla" za duzo, dobieramy brakujace liczby.
  if (wrong.length < 3) {
    const used = new Set([`${correctValue}`, ...wrong.map(String)]);
    let bump = 2;
    while (wrong.length < 3) {
      const candidate = `${correctValue + bump}`;
      if (!used.has(candidate)) {
        wrong.push(candidate);
        used.add(candidate);
      }
      bump += 1;
    }
  }

  const optionsRaw = [
    { latex: `${correctValue}`, isCorrect: true },
    ...wrong.map((w) => ({ latex: w, isCorrect: false })),
  ];

  // Dodatkowa asekuracja: w finalnej tablicy zawsze 4 opcje.
  if (optionsRaw.length < 4) {
    const used = new Set(optionsRaw.map((o) => String(o.latex)));
    let bump = 10;
    while (optionsRaw.length < 4) {
      const candidate = `${correctValue + bump}`;
      if (!used.has(candidate)) {
        optionsRaw.push({ latex: candidate, isCorrect: false });
        used.add(candidate);
      }
      bump += 1;
    }
  }

  const options = shuffle(optionsRaw).map((o, idx) => ({
    label: LETTERS[idx],
    latex: o.latex,
    isCorrect: o.isCorrect,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaLogSingleCoefClosedTask",
    data: {
      a,
      k,
      op,
      X,
      Y,
      t: correctValue,
      exprLatex,
      options,
      correctIndex,
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Liczba <InlineMath math={data.exprLatex} /> jest równa:
      </p>
      <div style={{ marginTop: 8 }}>
        {data.options.map((opt, idx) => (
          <div key={idx} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = data.options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = data.options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a, k, op, X, Y, t, exprLatex } = data;
  const yPowK = Math.pow(Y, k);

  const propertyLatex =
    op === "-"
      ? `\\log_{a}x-${k}\\log_{a}y=\\log_{a}\\left(\\tfrac{x}{y^{${k}}}\\right)`
      : `\\log_{a}x+${k}\\log_{a}y=\\log_{a}\\left(x\\cdot y^{${k}}\\right)`;

  const combinedLatex =
    op === "-"
      ? `\\tfrac{${X}}{${Y}^{${k}}}`
      : `${X}\\cdot ${Y}^{${k}}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Korzystamy z własności logarytmów:
      </div>
      <BlockMath math={propertyLatex} />

      <div>
        <strong>Krok 2.</strong> Podstawiamy:
      </div>
      <BlockMath math={`${exprLatex}=${logb(a)}\\left(${combinedLatex}\\right)`} />

      <div>
        <strong>Krok 3.</strong> Obliczamy argument logarytmu:
      </div>
      <BlockMath
        math={
          op === "-"
            ? `${combinedLatex}=\\tfrac{${X}}{${yPowK}}=${pow(a, t)}`
            : `${combinedLatex}=${X}\\cdot ${yPowK}=${pow(a, t)}`
        }
      />

      <div>
        <strong>Krok 4.</strong> Z definicji logarytmu:
      </div>
      <BlockMath math={`${logb(a)}\\left(${pow(a, t)}\\right)=${t}`} />
    </div>
  );
}
